import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import usePageMeta from "../lib/usePageMeta";

export default function Terms() {
  usePageMeta({
    title: "Terms of Service | American Asset Recovery Agency",
    description: "Terms of Service for American Asset Recovery Agency, including our contingency-fee structure and no upfront fees policy.",
    canonical: "https://americanassetrecoveryagency.com/terms",
  });
  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="terms-page">
      <Header />
      <main className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/" className="text-sm text-[#475569] hover:text-[#FF008F]" data-testid="back-home">← Back to site</Link>

          <p className="mt-8 text-sm font-bold uppercase tracking-[0.18em] text-[#FF008F]">Terms of Service</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#0F172A] leading-tight mt-3">
            The terms of using our website & services.
          </h1>
          <p className="mt-3 text-sm text-[#64748B]">Last updated: February 2026</p>

          <div className="mt-10 prose-content space-y-8 text-[#334155] leading-relaxed">
            <Section title="1. Acceptance of terms">
              <p>By accessing americanassetrecoveryagency.com or engaging American Asset Recovery Agency ("we," "us," "our") for surplus foreclosure funds recovery services, you ("you," "Client") agree to be bound by these Terms of Service. If you do not agree, please do not use this site.</p>
            </Section>

            <Section title="2. About our service">
              <p>We are a private firm that helps eligible former homeowners identify and recover excess proceeds from foreclosure sales. We are not a law firm and do not provide legal advice. We work alongside licensed attorneys in jurisdictions where representation is required.</p>
            </Section>

            <Section title="3. No upfront fees — contingency basis">
              <ul className="list-disc pl-6 space-y-1">
                <li>We charge <strong>no upfront fees</strong>. Our compensation is a percentage of the funds successfully recovered, clearly disclosed in a written engagement agreement before any work begins.</li>
                <li>If we do not recover funds for you, you owe us nothing.</li>
                <li>The contingency percentage and any reimbursable costs (filing fees, certified mail) will be itemized in your engagement letter.</li>
              </ul>
            </Section>

            <Section title="4. No guarantees">
              <p>Results vary case by case. Eligibility, deadlines, competing claims, and court schedules affect outcomes. While we work diligently on every accepted case, we cannot guarantee recovery, recovery amounts, or recovery timelines. Examples of past recoveries on this site are not promises of future results.</p>
            </Section>

            <Section title="5. Your representations">
              <p>By submitting information to us, you represent that:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>The information you provide is accurate to the best of your knowledge.</li>
                <li>You are the former owner of the property (or a legally authorized heir / representative) and have the right to claim any surplus funds.</li>
                <li>You are not under contract with another recovery firm for the same property.</li>
              </ul>
            </Section>

            <Section title="6. Authorization to act">
              <p>Before we file anything on your behalf, you will sign a written engagement letter and limited power of attorney specifying the scope of authority. We will not initiate court filings without your express written consent.</p>
            </Section>

            <Section title="7. Communication">
              <p>By submitting our forms or providing your phone number, you consent to be contacted by American Asset Recovery Agency by phone, SMS, or email regarding your potential claim. You may opt out at any time.</p>
            </Section>

            <Section title="8. SMS messaging terms &amp; conditions">
              <p>This section is a standalone agreement governing the SMS (text messaging) program operated by American Asset Recovery Agency. By opting in to receive SMS messages, you agree to these SMS terms.</p>

              <div className="rounded-xl border border-[#E2DDD3] bg-white p-5 space-y-2">
                <p>By providing your mobile number and opting in, you agree to receive customer care messages from <strong>AMERICAN ASSET RECOVERY AGENCY</strong>.</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Message frequency may vary. On average, 1–2 messages per month.</li>
                  <li>Message and data rates may apply.</li>
                  <li>You can opt out at any time by replying <strong>STOP</strong>.</li>
                  <li>For assistance, reply <strong>HELP</strong>.</li>
                  <li>Privacy Policy: <a href="https://www.americanassetrecoveryagency.com/privacy" className="text-[#1E3A8A] hover:text-[#FF008F] font-medium">https://www.americanassetrecoveryagency.com/privacy</a></li>
                </ul>
              </div>

              <div className="rounded-xl border border-[#E2DDD3] bg-white p-5 space-y-2">
                <p>By providing your mobile number, you agree to receive SMS messages from <strong>AMERICAN ASSET RECOVERY AGENCY</strong> related to customer care messages, delivery notification messages, and account notification messages.</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Message frequency:</strong> Message frequency may vary. On average, 1–2 messages per month.</li>
                  <li><strong>Message and data rates:</strong> Message and data rates may apply.</li>
                  <li><strong>Privacy Policy:</strong> <a href="https://www.americanassetrecoveryagency.com/privacy" className="text-[#1E3A8A] hover:text-[#FF008F] font-medium">https://www.americanassetrecoveryagency.com/privacy</a></li>
                </ul>
                <p><strong>Consent to receive SMS messages is not a condition of purchase or service.</strong></p>
              </div>

              <p><strong>8.1 SMS messaging service.</strong> By providing your phone number, you consent to receive SMS text messages from American Asset Recovery Agency for appointment reminders, case updates, marketing messages, and general two-way communication about surplus foreclosure funds recovery services. Message and Data Rates may apply.</p>

              <p><strong>8.2 Message frequency.</strong> You will receive more than one message from us unless you opt out. Messaging frequency varies, and on average you will receive <strong>1–2 messages per month</strong>. American Asset Recovery Agency reserves the right to alter the frequency of messages at any time to increase or decrease the total number of messages. American Asset Recovery Agency and mobile carriers are not liable for delays or undelivered messages.</p>

              <p><strong>8.3 Message and data rates.</strong> Message and data rates may apply based on your mobile carrier's terms.</p>

              <p><strong>8.4 Privacy.</strong> Your information will be handled in accordance with our <Link to="/privacy" className="text-[#1E3A8A] hover:text-[#FF008F] font-medium">Privacy Policy</Link>, including the SMS &amp; text messaging privacy section.</p>

              <p><strong>8.5 Cancellation / opt-out instructions.</strong> You can opt out of receiving SMS messages at any time by replying <strong>STOP</strong> to any message we send you. After you opt out of text messaging, you will receive one additional message confirming your request has been processed.</p>

              <p><strong>8.6 Help / customer support.</strong> Text the word <strong>HELP</strong> for support to <a href="sms:+18335359505" className="text-[#1E3A8A] hover:text-[#FF008F] font-medium">1-833-535-9505</a>. You may also contact us directly at <a href="mailto:recover@americanassetrecoveryagency.com" className="text-[#1E3A8A] hover:text-[#FF008F] font-medium">recover@americanassetrecoveryagency.com</a> or <a href="tel:+18335359505" className="text-[#1E3A8A] hover:text-[#FF008F] font-medium">1-833-535-9505</a>.</p>

              <p><strong>8.7 Liability.</strong> We are not responsible for any charges, errors, or delays in SMS delivery, or undelivered messages caused by your carrier or third-party service providers.</p>
            </Section>

            <Section title="9. Website content &amp; intellectual property">
              <p>All content on this website — text, images, branding, logos — is the property of American Asset Recovery Agency or licensed to us, and is protected by U.S. copyright and trademark laws. You may not copy or republish content without our written permission.</p>
            </Section>

            <Section title="10. Disclaimers">
              <p>This website and its content are provided "as is" without warranties of any kind. Information here is general and educational; it is not legal or financial advice. Always consult a licensed professional for advice specific to your situation.</p>
            </Section>

            <Section title="11. Limitation of liability">
              <p>To the maximum extent permitted by law, American Asset Recovery Agency is not liable for indirect, incidental, consequential, or punitive damages arising out of your use of this site. Our maximum liability under any engagement is limited to the fees you have paid us under that engagement.</p>
            </Section>

            <Section title="12. Governing law">
              <p>These Terms are governed by the laws of the State of Florida, without regard to its conflict-of-laws principles. Any dispute will be resolved in the state or federal courts located in Orange County, Florida.</p>
            </Section>

            <Section title="13. Changes to these terms">
              <p>We may update these Terms from time to time. The current version, marked by the date above, supersedes prior versions. Continued use of the site after changes means you accept the revised Terms.</p>
            </Section>

            <Section title="14. Contact">
              <p>
                American Asset Recovery Agency<br />
                Email: <a href="mailto:recover@americanassetrecoveryagency.com" className="text-[#1E3A8A] hover:text-[#FF008F] font-medium">recover@americanassetrecoveryagency.com</a><br />
                Phone: <a href="tel:+18335359505" className="text-[#1E3A8A] hover:text-[#FF008F] font-medium">1-833-535-9505</a><br />
                SMS support: Text <strong>HELP</strong> to <a href="sms:+18335359505" className="text-[#1E3A8A] hover:text-[#FF008F] font-medium">1-833-535-9505</a>
              </p>
            </Section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-serif-display text-2xl text-[#0F172A] mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
